import trI18n from '@/common/utils/trI18n';
import tr from '@/common/utils/tr';
import * as yup from 'yup';
import Constant from '@/common/constant/Constant';
import TelephoneConstant from '@/common/constant/TelephoneConstant';
import Utils from '@/common/utils/utils';
import { getRichTextFieldContent } from '@/component/ticketList/handler/TicketHandler';
import TimezoneUtils from '@/common/utils/timezoneUtils';

const BaseCustomSchema = (fieldListData) => {
    let validateObj = {};

    if (fieldListData.length) {
        fieldListData.forEach((field) => {
            if (!Utils.isEmptyObject(field)) {
                let schemaValidate;

                switch (field.type) {
                    // Field text
                    case Constant.FORM_TYPE.FIELD_TEXT:
                        schemaValidate = yup.string().trim();
                        if (field.structure.maxLength) {
                            schemaValidate = schemaValidate.test({
                                message: `${field.structure.maxLength}文字以下で入力してください。`,
                                name: 'maxLength',
                                test(value) {
                                    if (!value) {
                                        return true;
                                    }
                                    if (value.length <= Number(field.structure.maxLength)) {
                                        return true;
                                    } else {
                                        return false;
                                    }
                                },
                            });
                        }
                        if (field.structure.minLength) {
                            schemaValidate = schemaValidate.test({
                                message: `${field.structure.minLength}文字以上で入力してください。`,
                                name: 'minLength',
                                test(value) {
                                    if (!value) {
                                        return true;
                                    }
                                    if (value.length >= Number(field.structure.minLength)) {
                                        return true;
                                    } else {
                                        return false;
                                    }
                                },
                            });
                        }
                        if (field.structure.inputFormat) {
                            schemaValidate = schemaValidate.test({
                                message: `入力形式に誤りがあります。`,
                                name: 'input format',
                                test(value) {
                                    if (!value) {
                                        return true;
                                    }
                                    const inputFormat = field.structure.inputFormat;
                                    const regexString = new RegExp(inputFormat);;
                                    const isMatCh = regexString.test(value);

                                    if (!isMatCh) {
                                        return false;
                                    } else {
                                        return true;
                                    }
                                },
                            });
                        }
                        break;

                    // Field int
                    case Constant.FORM_TYPE.FIELD_INT:
                        schemaValidate = yup
                            .number()
                            .typeError(tr('数字を入力してください。'))
                            .nullable(true)
                            .transform((_, val) => (val ? Number(val) : null));

                        let maxValue = !Utils.isEmptyString(field.structure.maxValue)
                            ? field.structure.maxValue
                            : Constant.NUMBER_FIELD_DEFAULT.MAX_VALUE;
                        
                        // Special validation message for telephone_human_score field
                        const isTelephoneHumanScore =
                            field.sysField === TelephoneConstant.TELEPHONE.SYSTEM_FIELD.HUMAN_SCORE;
                        const customValidationMessage = isTelephoneHumanScore
                            ? trI18n('MSG-TELEPHONE-VALID-HUMAN-SCORE')
                            : null;
                        
                        schemaValidate = schemaValidate.test({
                            message:
                                customValidationMessage ||
                                trI18n('MSG-FIELD-VALID-MAX-VALUE').replace(
                                    Constant.FIELD_VALID_INTEGER.MAX_VALUE,
                                    maxValue
                                ),
                            name: 'maxValue',
                            test(value) {
                                if (!value) {
                                    return true;
                                }
                                if (value <= Number(maxValue)) {
                                    return true;
                                } else {
                                    return false;
                                }
                            },
                        });

                        let minValue = !Utils.isEmptyString(field.structure.minValue)
                            ? field.structure.minValue
                            : Constant.NUMBER_FIELD_DEFAULT.MIN_VALUE;
                        schemaValidate = schemaValidate.test({
                            message:
                                customValidationMessage ||
                                trI18n('MSG-FIELD-VALID-MIN-VALUE').replace(
                                    Constant.FIELD_VALID_INTEGER.MIN_VALUE,
                                    minValue
                                ),
                            name: 'minValue',
                            test(value) {
                                if (!value) {
                                    return true;
                                }
                                if (value >= Number(minValue)) {
                                    return true;
                                } else {
                                    return false;
                                }
                            },
                        });

                        if (field.structure.integerOnly === true) {
                            schemaValidate = schemaValidate.test({
                                message: customValidationMessage || trI18n('MSG-FIELD-VALID-INTEGER-ONLY'),
                                name: 'integerOnly',
                                test(value) {
                                    if (!value) {
                                        return true;
                                    }
                                    value = value.toString();

                                    const integerRegex = /^[-+]?\d+$/;
                                    const isInteger = integerRegex.test(value);

                                    if (!isInteger) {
                                        return false;
                                    } else {
                                        return true;
                                    }
                                },
                            });
                        }
                        break;

                    // Field date
                    case Constant.FORM_TYPE.FIELD_DATE:
                        schemaValidate = yup.string();
                    schemaValidate = schemaValidate.test({
                        message: trI18n('USER07MSG001'),
                        name: 'dateFormat',
                        test(value) {
                            if (value === undefined) {
                                return true;
                            }
                                if (value === null || value.length === 0 || value === '') {
                                    return true;
                                }

                                const dateFormat = field.structure.dateFormat;
                                // TZL-001:「年月日＋時間（TimeZone）」— the form value is the UTC ISO string set by DataPickerInput.
                                // A value saved with another display type (not converted when the type is switched) fails here
                                // and must be re-entered, same as the existing behavior between the other display types
                                if (dateFormat === Constant.SETTING_DATE_FIELD_FORMAT.YYYYMMDD_HHMM_TZ) {
                                    return TimezoneUtils.isUtcIsoString(value);
                                }
                                let formatRegex;
                                switch (dateFormat) {
                                    case Constant.SETTING_DATE_FIELD_FORMAT.YYYYMMDD_HHMM:
                                        formatRegex =
                                            /^(\d{4})[/](\d{2})[/](\d{2}) (\d{2}):(\d{2})*$/;
                                        break;
                                    case Constant.SETTING_DATE_FIELD_FORMAT.YYYYMMDD:
                                        formatRegex = /^(\d{4})[/](\d{2})[/](\d{2})*$/;
                                        break;
                                    case Constant.SETTING_DATE_FIELD_FORMAT.YYYYMM:
                                        formatRegex = /^(\d{4})[/](\d{2})*$/;
                                        break;
                                    case Constant.SETTING_DATE_FIELD_FORMAT.YYYY:
                                        formatRegex = /^(\d{4})*$/;
                                        break;
                                    case Constant.SETTING_DATE_FIELD_FORMAT.HHMM:
                                        formatRegex = /^(\d{2}):(\d{2})*$/;
                                        break;
                                    case Constant.SETTING_DATE_FIELD_FORMAT.YYYYMMDD_HHMMSS:
                                        formatRegex = /^(\d{4})[/](\d{2})[/](\d{2}) (\d{2}):(\d{2}):(\d{2})*$/;
                                        break;
                                    case Constant.SETTING_DATE_FIELD_FORMAT.HHMMSS:
                                        formatRegex = /^(\d{2}):(\d{2}):(\d{2})*$/;
                                        break;
                                    default:
                                        formatRegex =
                                            /^(\d{4})[/](\d{2})[/](\d{2}) (\d{2}):(\d{2})*$/;
                                }

                                const isValid = formatRegex.test(value);

                                if (!isValid) {
                                    return false;
                                } else {
                                    return true;
                                }
                            },
                        });
                        break;

                    // Field iframe
                    case Constant.FORM_TYPE.FIELD_IFRAME:
                        schemaValidate = yup.string();
                        schemaValidate = schemaValidate.test({
                            message: trI18n('COMMSG015'),
                            name: 'iframeFormat',
                            test(value) {
                                if (!value) {
                                    return true;
                                }

                                const isValid = Utils.validateURL(value);
                                if (!isValid) {
                                    return false;
                                }

                                return true;
                            },
                        });
                        break;

                    // Field URL
                    case Constant.FORM_TYPE.FIELD_URL:
                        schemaValidate = yup
                            .string()
                            .transform(value => (Array.isArray(value) ? value[0] : value))

                        schemaValidate = schemaValidate.test({
                            message: trI18n('COMMSG015'),
                            name: 'urlFormat',
                            test(value) {
                                if (!value) {
                                    return true;
                                }

                                let arrUrl = value.split(/\r?\n/);
                                let statusValidate = true;

                                if (Utils.isEmptyArray(arrUrl)) {
                                    return;
                                }
                                for (let i = 0; i < arrUrl.length; i++) {
                                    if (arrUrl[i]) {
                                        statusValidate = Utils.validateURL(arrUrl[i]);
                                        if (!statusValidate) {
                                            break;
                                        }
                                    }
                                }

                                return statusValidate;
                            },
                        });
                        break;
                    
                    // Field RichText
                    case Constant.FORM_TYPE.FIELD_RICHTEXT:
                        schemaValidate = yup.string();

                        if (field?.structure?.required) {
                            schemaValidate = schemaValidate.test({
                                message: trI18n('COMM01MSG11'),
                                name: 'validateRequire',
                                test(value) {
                                    if (!getRichTextFieldContent(value)) {
                                        return false;
                                    }
                                    return true;
                                },
                            });
                        }

                        // Validate max length
                        // if (field?.structure?.maxLength) {
                        //     let maxLength = field?.structure?.maxLength;
                        //     schemaValidate = schemaValidate.test({
                        //         message: `${maxLength}文字以下で入力してください。`,
                        //         name: 'validateRequire',
                        //         test(value) {
                        //             let textContent = getRichTextFieldContent(value);
                        //             if (textContent.length > parseInt(maxLength)) {
                        //                 return false;
                        //             }
                        //             return true;
                        //         },
                        //     });
                        // }

                        break;

                    case Constant.FORM_TYPE.FIELD_TAG:
                        schemaValidate = yup.array();
                        if (field.structure.required) {
                            schemaValidate = schemaValidate.min(1, trI18n('COMM01MSG11'));
                        }
                        break;
                    case Constant.FORM_TYPE.MERMAID:
                        schemaValidate = yup.string().trim();
                        if (field?.structure?.required) {
                            schemaValidate = schemaValidate.test({
                                message: trI18n('COMMSG014'),
                                name: 'validateRequire',
                                test(value) {
                                    if (!value) {
                                        return false;
                                    }
                                    return true;
                                },
                            });
                        }
                        break;
                    case Constant.FORM_TYPE.HTML:
                        schemaValidate = yup.string().trim();
                        if (field?.structure?.required) {
                            schemaValidate = schemaValidate.test({
                                message: trI18n('COMMSG014'),
                                name: 'validateRequire',
                                test(value) {
                                    if (!value) {
                                        return false;
                                    }
                                    return true;
                                },
                            });
                        }
                        break;
                    case Constant.FORM_TYPE.MARKDOWN:
                        schemaValidate = yup.string().trim();
                        if (field?.structure?.required) {
                            schemaValidate = schemaValidate.test({
                                message: trI18n('COMMSG014'),
                                name: 'validateRequire',
                                test(value) {
                                    if (!value) {
                                        return false;
                                    }
                                    return true;
                                },
                            });
                        }
                        break;
                    case Constant.FORM_TYPE.JSON:
                        schemaValidate = yup.string().trim();
                        if (field?.structure?.required) {
                            schemaValidate = schemaValidate.test({
                                message: trI18n('COMMSG014'),
                                name: 'validateRequire',
                                test(value) {
                                    if (!value) {
                                        return false;
                                    }
                                    return true;
                                },
                            });
                        }
                        break;
                    default:
                        break;
                }

                if (schemaValidate && field.structure.required) {
                    schemaValidate = schemaValidate.required(trI18n('COMM01MSG11'));
                }

                let msgCodeFormat = 'COMM01MSG08';
                if (field.definition === Constant.FORM_DEFINITION.SYSTEM && ['customerEmail', 'adminMailAdress'].includes(field.id)) {
                    msgCodeFormat = 'COMM01MSG07';
                }
                if (field.structure.regExpr) {
                    schemaValidate = schemaValidate.matches(
                        field.structure.regExpr,
                        trI18n(msgCodeFormat)
                    );
                }

                if (schemaValidate) {
                    validateObj[field.id] = schemaValidate;
                }
            }
        });
    }

    return validateObj;
};

export default BaseCustomSchema;
